import React, { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { ComboboxData, Option, OptionValue } from "components/ui/combobox/types";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useDebouncedValue } from "lib/debounce";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useSignalSettingCountryCodes } from "lib/hooks/api/teams/useSignalSettings";
import { routes } from "lib/routes";
import { OrgSortBy, OrgSortOrder, SearchOrgRole } from "lib/types/graphQLEnums";
import { ComboboxProps } from "../../combobox/ComboBox";
import { EntityOption } from "./ComboboxOption";
import { EmptyComboboxTrigger } from "./EmptyComboboxTrigger";
import { SelectedEntityCard } from "./SelectedEntityCard";

export type Buyer = {
  id: string;
  name: string;
};

type BuyerOption = Option & { isCustom?: boolean };

function ComboboxContentComponent(props: {
  combobox: ComboboxData;
  placeholder: string;
  setIsOpen: (open: boolean) => void;
  setSearchText: (text: string) => void;
  disabled?: boolean;
  loading: boolean;
  getBuyer: (id: string) => Buyer | null;
  onAddCustomBuyer?: (name: string) => void;
  trigger?: React.ReactNode;
}) {
  const {
    trigger,
    combobox,
    placeholder,
    setIsOpen,
    setSearchText,
    disabled,
    loading,
    getBuyer,
    onAddCustomBuyer,
  } = props;

  return (
    <ComboboxContent
      open={combobox.open}
      setOpen={(open: boolean) => {
        combobox.setOpen(open);
        setIsOpen(open);
      }}
      trigger={trigger ? trigger : <div />}
    >
      <ComboboxInput
        placeholder={placeholder}
        filterValue={combobox.filterValue}
        setFilterValue={(value) => {
          combobox.setFilterValue(value);
          setSearchText(value);
        }}
        open={combobox.open}
        setOpen={combobox.setOpen}
        value={combobox.value}
        items={combobox.items}
        multiple={combobox.multiple}
        inputRef={combobox.inputRef}
        inputElementRef={combobox.inputElementRef}
        disabled={disabled}
      />
      <ComboboxEmpty
        filteredItems={combobox.filteredItems}
        filterValue={combobox.filterValue}
        disableAddingOptions={!onAddCustomBuyer || loading}
        onAddItem={!loading ? onAddCustomBuyer : undefined}
        addNewOption={
          !loading && onAddCustomBuyer
            ? (filterValue, onAdd) => (
                <AddCustomBuyerButtonWrapper>
                  <Button
                    buttonType="tertiary"
                    blackText
                    icon="plus"
                    iconProps={{ color: "sysTextDefault" }}
                    onClick={onAdd}
                    justifyContent="start"
                    fullWidth={true}
                  >
                    Add {filterValue}
                  </Button>
                </AddCustomBuyerButtonWrapper>
              )
            : undefined
        }
        containerPadding={0}
      >
        {loading ? <SkeletonText width="100%" /> : null}
      </ComboboxEmpty>

      <ListScrollArea>
        <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
          <ComboboxCollection options={combobox.filteredItems as BuyerOption[]}>
            {(option: BuyerOption) => {
              const buyer = getBuyer(String(option.value));
              return (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={combobox.value}
                  selectItem={combobox.selectItem}
                  multiple={combobox.multiple}
                  highlightedIndex={combobox.highlightedIndex}
                  filteredItems={combobox.filteredItems}
                >
                  <EntityOption
                    label={buyer?.name || option.label}
                    badge={option.isCustom ? "Freetext" : undefined}
                    iconProps={{
                      color: option.isCustom ? "sysTextInactive" : "sysPrimaryDefault",
                      backgroundColor: option.isCustom
                        ? "sysBackgroundInactive"
                        : "sysPrimarySubtle",
                    }}
                  />
                </ComboboxItem>
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
      </ListScrollArea>
    </ComboboxContent>
  );
}

type BuyersComboboxProps<TFieldValues extends FieldValues = FieldValues> = Required<
  Pick<ComboboxProps<TFieldValues>, "control" | "name">
> & {
  placeholder?: string;
  trigger?: React.ReactNode;
  // Resolves custom buyer IDs (in the RHF field) to names for display
  customBuyers?: Buyer[];
  onAddCustomBuyer?: (name: string) => void;
};

export function BuyersCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  placeholder = "Search buyers...",
  customBuyers = [],
  onAddCustomBuyer,
  trigger,
}: BuyersComboboxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const [isOpen, setIsOpen] = useState(false);
  const [clickedBuyerId, setClickedBuyerId] = useState<string | null>(null);
  const prevBuyersCountRef = useRef(0);

  const userCountryCodes = useSignalSettingCountryCodes();

  const customBuyerIds = useMemo(() => new Set(customBuyers.map((b) => b.id)), [customBuyers]);

  // field.value contains all selected buyer IDs (Stotles + custom)
  const stotlesBuyerIds = useMemo(
    () => (field.value ?? []).filter((id: string) => !customBuyerIds.has(id)),
    [field.value, customBuyerIds],
  );

  // Fetch selected buyers by ID (when we have IDs but no search)
  const fetchSelectedBuyers = stotlesBuyerIds.length > 0 && !debouncedText && !isOpen;

  const { data: selectedBuyers, isFetching: isFetchingSelected } = useOrganisations(
    {
      limit: stotlesBuyerIds.length || 20,
      page: 1,
      // No countryCodes: an already-selected buyer must resolve its name even when it sits
      // outside the user's feed countries.
      ids: stotlesBuyerIds,
      sortBy: OrgSortBy.NoticeCount,
      sortOrder: OrgSortOrder.Desc,
      role: SearchOrgRole.Buyer,
      include: { awardCount: false, noticeCount: true, aliases: false },
    },
    {
      enabled: fetchSelectedBuyers,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  // Persistent cache of all known buyers from both queries.
  // Entries are only added, never removed — prevents cards from flashing when a query is
  // disabled (e.g. selectedBuyers is disabled while the dropdown is open).
  const [knownBuyers, setKnownBuyers] = useState<Map<string, Buyer>>(new Map());

  // Search for buyers based on query or show initial set when opening dropdown
  const { data: dropdownBuyers, isFetching: isFetchingIds } = useOrganisations(
    {
      limit: 20,
      page: 1,
      names: debouncedText ? [debouncedText] : undefined,
      countryCodes: userCountryCodes,
      sortBy: OrgSortBy.NoticeCount,
      sortOrder: OrgSortOrder.Desc,
      role: SearchOrgRole.Buyer,
      include: { awardCount: false, noticeCount: true, aliases: false },
    },
    { enabled: !fetchSelectedBuyers || isOpen },
  );

  // Accumulate buyer data from both queries into the persistent cache
  useEffect(() => {
    const allOrgs = [
      ...(selectedBuyers?.searchOrganisations.orgs ?? []),
      ...(dropdownBuyers?.searchOrganisations.orgs ?? []),
    ];
    if (allOrgs.length === 0) return;

    setKnownBuyers((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const org of allOrgs) {
        if (!next.has(org.id)) {
          next.set(org.id, { id: org.id, name: org.name });
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedBuyers, dropdownBuyers]);

  // Display buyers = all selected IDs resolved to Buyer objects.
  // Custom buyer IDs are resolved via the customBuyers prop; Stotles buyer IDs via knownBuyers cache.
  const displayBuyers: Buyer[] = useMemo(
    () =>
      (field.value ?? [])
        .map((id: string) => {
          if (customBuyerIds.has(id)) return customBuyers.find((b) => b.id === id) ?? null;
          return knownBuyers.get(id) ?? null;
        })
        .filter((b): b is Buyer => b !== null),
    [field.value, knownBuyers, customBuyers, customBuyerIds],
  );

  // Keep combobox open when a buyer is added from empty state
  useEffect(() => {
    const prevCount = prevBuyersCountRef.current;
    const currentCount = displayBuyers.length;

    // If we went from 0 to 1+ buyers and combobox is open, set clicked ID to the first buyer
    if (prevCount === 0 && currentCount > 0 && isOpen) {
      setClickedBuyerId(displayBuyers[0].id);
    }

    prevBuyersCountRef.current = currentCount;
  }, [displayBuyers, isOpen]);

  const options: BuyerOption[] = useMemo(() => {
    const searchBuyers = dropdownBuyers?.searchOrganisations.orgs || [];

    // When searching:
    // - Show matching Stotles buyers from the main GraphQL query.
    // - Show matching custom buyers, filtered on client-side.
    //   For v1, custom buyers are scoped to already-selected ones only, so no server search is performed.
    if (debouncedText) {
      const matchingCustomBuyers = customBuyers.filter((b) =>
        b.name.toLowerCase().includes(debouncedText.toLowerCase()),
      );
      return [
        ...matchingCustomBuyers.map((b) => ({ value: b.id, label: b.name, isCustom: true })),
        ...searchBuyers.map((b) => ({ value: b.id, label: b.name })),
      ];
    }

    // When not searching, show selected custom buyers first, then selected Stotles buyers, then other results
    const selectedOrgs = selectedBuyers?.searchOrganisations.orgs || [];

    const customOptions: BuyerOption[] = customBuyers.map((b) => ({
      value: b.id,
      label: b.name,
      isCustom: true,
    }));

    // Create a map to avoid duplicates
    const buyerMap = new Map<string, BuyerOption>();

    selectedOrgs.forEach((buyer) => {
      if (!displayBuyers.some((b) => b.id === buyer.id)) {
        buyerMap.set(buyer.id, { value: buyer.id, label: buyer.name });
      }
    });

    searchBuyers.forEach((buyer) => {
      if (!buyerMap.has(buyer.id) && !displayBuyers.some((b) => b.id === buyer.id)) {
        buyerMap.set(buyer.id, { value: buyer.id, label: buyer.name });
      }
    });

    const stotlesDisplayOptions: BuyerOption[] = (field.value ?? [])
      .filter((id: string) => !customBuyerIds.has(id))
      .map((id: string) => knownBuyers.get(id))
      .filter((b: Buyer | undefined): b is Buyer => b !== undefined)
      .map((b: Buyer) => ({ value: b.id, label: b.name }));

    return [...customOptions, ...stotlesDisplayOptions, ...buyerMap.values()];
  }, [
    debouncedText,
    dropdownBuyers,
    selectedBuyers,
    displayBuyers,
    customBuyers,
    customBuyerIds,
    field.value,
    knownBuyers,
  ]);

  const getBuyer = useMemo(
    () =>
      (id: string): Buyer | null => {
        if (customBuyerIds.has(id)) {
          return customBuyers.find((b) => b.id === id) ?? null;
        }
        const allBuyers = [
          ...(selectedBuyers?.searchOrganisations.orgs || []),
          ...(dropdownBuyers?.searchOrganisations.orgs || []),
        ];
        const buyer = allBuyers.find((b) => b.id === id);
        if (!buyer) return null;
        return { id: buyer.id, name: buyer.name };
      },
    [selectedBuyers, dropdownBuyers, customBuyers, customBuyerIds],
  );

  const loading =
    (isFetchingIds && !fetchSelectedBuyers && !dropdownBuyers) ||
    (isFetchingSelected && fetchSelectedBuyers && !selectedBuyers);

  // Convert field value (array of strings) to array of OptionValue
  const comboboxValue: OptionValue[] = field.value ?? [];

  const handleChange = (newValues: OptionValue[]) => {
    field.onChange(newValues);
  };

  const openCombobox = (combobox: ComboboxData) => {
    combobox.setOpen(true);
    setIsOpen(true);
  };

  return (
    <div>
      <Combobox
        items={options}
        value={comboboxValue}
        onChange={handleChange}
        multiple
        externalFilter={!loading}
      >
        {(combobox) => (
          <PropertyInnerContainer>
            <Flex column gap={8} width="100%" minWidth={0}>
              {displayBuyers.length > 0 ? (
                displayBuyers.map((buyer) => {
                  const isCustomBuyer = customBuyerIds.has(buyer.id);
                  return (
                    <React.Fragment key={buyer.id}>
                      <SelectedEntityCard
                        entity={buyer}
                        dataType="buyer"
                        combobox={combobox}
                        setIsOpen={setIsOpen}
                        setEntityClickerId={setClickedBuyerId}
                        clickedEntityId={clickedBuyerId}
                        entityButtonProps={{
                          iconColor: isCustomBuyer ? "sysTextInactive" : "sysPrimaryDefault",
                          iconBackgroundColor: isCustomBuyer
                            ? "sysBackgroundInactive"
                            : "sysPrimarySubtle",
                        }}
                        linkButtonProps={{
                          href: isCustomBuyer ? undefined : `${routes.buyer}/${buyer.id}`,
                          tooltip: isCustomBuyer
                            ? "No profile for this buyer as it has been added manually"
                            : undefined,
                        }}
                      />
                      {clickedBuyerId === buyer.id && (
                        <ComboboxContentComponent
                          combobox={combobox}
                          placeholder={placeholder}
                          setIsOpen={setIsOpen}
                          setSearchText={setSearchText}
                          loading={loading}
                          getBuyer={getBuyer}
                          onAddCustomBuyer={onAddCustomBuyer}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <ComboboxContentComponent
                  combobox={combobox}
                  placeholder={placeholder}
                  setIsOpen={setIsOpen}
                  setSearchText={setSearchText}
                  disabled={isFetchingSelected}
                  loading={loading}
                  getBuyer={getBuyer}
                  onAddCustomBuyer={onAddCustomBuyer}
                  trigger={
                    trigger ? (
                      // Wrap trigger in a span to handle onClick and onKeyDown for accessibility
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => openCombobox(combobox)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openCombobox(combobox);
                          }
                        }}
                      >
                        {trigger}
                      </span>
                    ) : (
                      <EmptyComboboxTrigger
                        label="Buyer"
                        disabled={isFetchingSelected}
                        onClick={() => {
                          openCombobox(combobox);
                        }}
                      />
                    )
                  }
                />
              )}
            </Flex>
          </PropertyInnerContainer>
        )}
      </Combobox>
    </div>
  );
}

const PropertyInnerContainer = styled.div({
  minWidth: 0,
  width: "100%",
});

const ListScrollArea = styled.div({
  maxHeight: 390,
  overflowY: "auto",
});

const AddCustomBuyerButtonWrapper = styled(Flex)({
  width: "100%",
  alignSelf: "stretch",
});
